import type { StyledComponent } from '@emotion/styled';
import type { StringMap } from 'corgi-types';

import { ScaleAliasPrefix } from '../enums/ScaleAlias';
import { generateScaleAliases } from '../utils/scaleAliases';
import figmaColors from './colors';
import opacity from './sys/opacity';

/**
 * Based of the Design System update research made by the Frontend Team
 * https://home24.atlassian.net/wiki/spaces/TT/pages/222167527/Design+system+update+research
 * We decided to start a process of updating our design system using `Scale Aliases`
 * a feature of the `styled-system` library.
 *
 * Based on this decision, initially, any new values ​​that need to be added to the style properties
 * `space`, `fontSizes`, and `lineHeights` must be added to the `extendedProps` property below.
 */
const extendedProps = {
  space: [2, 6, 10, 12, 20, 40],
  fontSizes: [10, 20, 22, 28],
  lineHeights: ['14px', '18px', '22px', '28px', '36px'],
};

export const breakpoints = ['632px', '984px', '1392px'];

/**
 * Scale Aliases are generated for the following style properties below:
 * `space`, `fontSizes` and `lineHeights`.
 */
const space = generateScaleAliases(
  ScaleAliasPrefix.Space,
  [0, 4, 8, 16, 24, 32, 48, 64, 96, 128, 192, 256, 320],
  extendedProps.space
);

const fontSizes = generateScaleAliases(
  ScaleAliasPrefix.FontSizes,
  [11, 12, 13, 14, 16, 18, 24, 32, 40, 48],
  extendedProps.fontSizes
);

const lineHeights = generateScaleAliases(
  ScaleAliasPrefix.LineHeights,
  [1, '16px', '20px', '24px', '32px', '40px', '48px', '56px', '64px'],
  extendedProps.lineHeights
);

const fonts = {
  'proxima-nova': 'proxima-nova, Arial, Helvetica, sans-serif',
  fallback: 'Arial, Helvetica, sans-serif',
};

const colors = {
  ...figmaColors,
  overlay: {
    default: 'rgba(0, 0, 0, 0.25)',
    dark: 'rgba(44, 54, 56, 0.6)',
    light: 'rgba(0, 0, 0, 0.1)',
    white: 'rgba(255, 255, 255, 0.7)',
  },
};

/**
 * @deprecated
 * Use `borderWidths` with `colors` instead
 */
const borders = [
  '', // NOTE: `borders.0` to `borders.2` are replaced with new tokens `borderWidths` and `colors`
  '', // adding the empty strings to keep the `borders.3` usages unchanged
  '',
  '1px solid #d8d8d8', // borders.3
];

const borderWidths = {
  small: '1px',
  medium: '2px',
  large: '4px',
};

/**
 * @deprecated
 * Use `shadows` instead
 */
const boxShadows = [
  'inset 0 0 0 1px #565d60',
  'inset 0 -1px 0 #e2e4e4',
  '0 1px 15px 0 rgba(13, 27, 34, 0.2)',
  '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
];

const shadows = {
  top: 'inset 0 1px 0 #e2e4e4',
  bottom: 'inset 0 -1px 0 #e2e4e4',
  left: 'inset 1px 0 0 #e2e4e4',
  right: 'inset -1px 0 0 #e2e4e4',
  extraLow: '0 0 7px 0 rgba(0, 0, 0, .10)',
  low: '0 1px 8px 0 rgba(0, 0, 0, .2)',
  lowBottom: '0 4px 4px 0 rgba(0, 0, 0, .1)',
  lowTop: '0 -3px 3px 0 rgba(0, 0, 0, .08)',
  high: '0 1px 15px 0 rgba(0, 0, 0, .2)',
};

const radii = {
  'x-small': '1px',
  small: '3px',
  medium: '4px',
  large: '12px',
  'x-large': '24px',
  table: '3px 3px 0px 0px',
  circle: '100%',
};

export enum BrandTheme {
  h24 = 'h24',
}

const width = {
  [1 / 5]: '20%',
  [1 / 4]: '25%',
  [1 / 3]: '33.3%',
  [1 / 2]: '50%',
  [2 / 3]: '66.6%',
  [3 / 4]: '75%',
  1: '100%',
};

export interface ThemeProps {
  breakpoints: string[];
  colors: typeof colors;
  space: StringMap<number>;
  borders: string[];
  radii: keyof typeof radii;
  boxShadows: string[];
  fonts: StringMap<string>;
  fontSizes: StringMap<number>;
  minWidth: string;
  minWidths: string[];
  maxWidth: string;
  maxWidths: string[];
  lineHeights: StringMap<number | string>;
  shadows: keyof typeof shadows;
  opacity: keyof typeof opacity;
  isHome24?: boolean;
  borderWidths: keyof typeof borderWidths;
}

/**
 * @deprecated Please use inline notation for styled components typings
 *
 * @example
 * const StyledDiv = styled.div<DisplayProps>`
 *   ${display};
 * `;
 */
export type ThemedComponent<T, P = object> = StyledComponent<T, P, { theme?: ThemeProps }>;

export type WithTheme<T = object> = T & { theme?: ThemeProps };

export default {
  breakpoints,
  space,
  colors,
  borders,
  boxShadows,
  fonts,
  fontSizes,
  lineHeights,
  shadows,
  radii,
  opacity,
  width,
  borderWidths,
};
